import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { FileUp, Loader2, Plus, Trash2, Pencil, Save, X, Download, ExternalLink, Sparkles, FileText, Eye, AlertTriangle, CheckCircle2 } from "lucide-react";
import { marked } from "marked";
import { renderPremiumGuidePdf } from "@/lib/premiumGuidePdf";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const REQUIRED_SECTIONS = [
  "Overview",
  "Learning Objectives",
  "Module",
  "Action Plan",
  "Quiz",
  "FAQ",
];

const ACCENT_PRESETS = [
  { name: "Gold", value: "#fbbf24" },
  { name: "Amber", value: "#f59e0b" },
  { name: "Emerald", value: "#10b981" },
  { name: "Sky", value: "#0ea5e9" },
  { name: "Indigo", value: "#6366f1" },
  { name: "Rose", value: "#f43f5e" },
  { name: "Slate", value: "#0f172a" },
];

interface LintIssue {
  level: "error" | "warning";
  message: string;
}

function lintMarkdown(md: string): LintIssue[] {
  const issues: LintIssue[] = [];
  const text = md || "";
  if (!text.trim()) {
    issues.push({ level: "error", message: "Markdown is empty." });
    return issues;
  }
  const lines = text.split("\n");
  const h1Count = lines.filter((l) => /^#\s+/.test(l)).length;
  const h2Count = lines.filter((l) => /^##\s+/.test(l)).length;
  if (h1Count === 0) issues.push({ level: "error", message: "Missing top-level H1 heading (`# Title`)." });
  if (h1Count > 1) issues.push({ level: "warning", message: `Multiple H1 headings found (${h1Count}). PDFs should have exactly one.` });
  if (h2Count < 3) issues.push({ level: "warning", message: `Only ${h2Count} H2 sections found. A premium blueprint typically has 5+ sections.` });

  const lower = text.toLowerCase();
  REQUIRED_SECTIONS.forEach((section) => {
    if (!lower.includes(section.toLowerCase())) {
      issues.push({ level: "warning", message: `Missing recommended section: "${section}".` });
    }
  });

  // Unclosed code fences
  const fenceCount = (text.match(/^```/gm) || []).length;
  if (fenceCount % 2 !== 0) {
    issues.push({ level: "error", message: "Unclosed code fence (```) detected." });
  }

  // Broken markdown links [text]( with no closing paren on same line
  lines.forEach((line, i) => {
    const opens = (line.match(/\]\(/g) || []).length;
    const closes = (line.match(/\)/g) || []).length;
    if (opens > closes) {
      issues.push({ level: "warning", message: `Possibly broken link on line ${i + 1}.` });
    }
  });

  const wordCount = text.replace(/[#>*_`\-]/g, " ").split(/\s+/).filter(Boolean).length;
  if (wordCount < 800) {
    issues.push({ level: "warning", message: `Content is short (~${wordCount} words). Premium blueprints should be 1500+ words.` });
  }

  return issues;
}

interface CoverPreset {
  id: string;
  name: string;
  subtitle: string;
  accentColor: string;
}

const presetStorageKey = (userId: string) => `pg-cover-presets:${userId || "anon"}`;

const loadPresets = (userId: string): CoverPreset[] => {
  try {
    const raw = localStorage.getItem(presetStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const savePresets = (userId: string, presets: CoverPreset[]) => {
  try {
    localStorage.setItem(presetStorageKey(userId), JSON.stringify(presets));
  } catch {
    /* ignore quota errors */
  }
};

interface Resource {
  id: string;
  blog_post_id: string;
  name: string;
  description: string | null;
  file_url: string;
  file_format: string | null;
  file_size: string | null;
  order_index: number;
  download_count: number;
}

interface Props {
  blogPostId: string;
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const PremiumGuideManager = ({ blogPostId }: Props) => {
  const { toast } = useToast();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMarkdown, setPreviewMarkdown] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewSubtitle, setPreviewSubtitle] = useState("Premium Blueprint • RunoBlog Learning System");
  const [accentColor, setAccentColor] = useState("#fbbf24");
  const [includeReflection, setIncludeReflection] = useState(true);
  const [userId, setUserId] = useState<string>("");
  const [coverPresets, setCoverPresets] = useState<CoverPreset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string>("");
  const [newPresetName, setNewPresetName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    file_url: "",
    file_format: "",
    file_size: "",
  });

  const fetchResources = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blog_post_resources" as any)
      .select("*")
      .eq("blog_post_id", blogPostId)
      .order("order_index", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } else {
      setResources((data as any) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (blogPostId && blogPostId !== "new") fetchResources();
  }, [blogPostId]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const uid = data.user?.id || "";
      setUserId(uid);
      setCoverPresets(loadPresets(uid));
    });
  }, []);

  const handleSavePreset = () => {
    const name = newPresetName.trim();
    if (!name) {
      toast({ variant: "destructive", title: "Name required", description: "Give your preset a name." });
      return;
    }
    const existing = coverPresets.find((p) => p.name.toLowerCase() === name.toLowerCase());
    let next: CoverPreset[];
    if (existing) {
      next = coverPresets.map((p) =>
        p.id === existing.id ? { ...p, subtitle: previewSubtitle, accentColor } : p,
      );
      toast({ title: "Preset updated", description: `"${name}" was overwritten.` });
    } else {
      const preset: CoverPreset = {
        id: crypto.randomUUID(),
        name,
        subtitle: previewSubtitle,
        accentColor,
      };
      next = [...coverPresets, preset];
      setSelectedPresetId(preset.id);
      toast({ title: "Preset saved", description: `"${name}" added to your presets.` });
    }
    setCoverPresets(next);
    savePresets(userId, next);
    setNewPresetName("");
  };

  const handleApplyPreset = (id: string) => {
    setSelectedPresetId(id);
    const preset = coverPresets.find((p) => p.id === id);
    if (!preset) return;
    setPreviewSubtitle(preset.subtitle);
    setAccentColor(preset.accentColor);
  };

  const handleDeletePreset = (id: string) => {
    const preset = coverPresets.find((p) => p.id === id);
    if (!preset) return;
    if (!confirm(`Delete preset "${preset.name}"?`)) return;
    const next = coverPresets.filter((p) => p.id !== id);
    setCoverPresets(next);
    savePresets(userId, next);
    if (selectedPresetId === id) setSelectedPresetId("");
    toast({ title: "Preset deleted" });
  };

  const resetForm = () => {
    setForm({ name: "", description: "", file_url: "", file_format: "", file_size: "" });
    setEditingId(null);
  };

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "";
      const path = `${blogPostId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("blog-resources")
        .upload(path, file, { upsert: false, contentType: file.type });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("blog-resources").getPublicUrl(path);
      setForm((prev) => ({
        ...prev,
        file_url: pub.publicUrl,
        name: prev.name || file.name,
        file_format: ext.toUpperCase(),
        file_size: formatBytes(file.size),
      }));
      toast({ title: "Uploaded", description: "File uploaded successfully." });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Upload failed", description: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.file_url.trim()) {
      toast({ variant: "destructive", title: "Missing fields", description: "Name and file URL are required." });
      return;
    }
    try {
      if (editingId) {
        const { error } = await supabase
          .from("blog_post_resources" as any)
          .update({
            name: form.name,
            description: form.description || null,
            file_url: form.file_url,
            file_format: form.file_format || null,
            file_size: form.file_size || null,
          })
          .eq("id", editingId);
        if (error) throw error;
        toast({ title: "Updated", description: "Resource updated." });
      } else {
        const { error } = await supabase.from("blog_post_resources" as any).insert({
          blog_post_id: blogPostId,
          name: form.name,
          description: form.description || null,
          file_url: form.file_url,
          file_format: form.file_format || null,
          file_size: form.file_size || null,
          order_index: resources.length,
        });
        if (error) throw error;
        toast({ title: "Added", description: "Resource added to Premium Guide." });
      }
      resetForm();
      fetchResources();
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    }
  };

  const handleEdit = (r: Resource) => {
    setEditingId(r.id);
    setForm({
      name: r.name,
      description: r.description || "",
      file_url: r.file_url,
      file_format: r.file_format || "",
      file_size: r.file_size || "",
    });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this resource?")) return;
    const { error } = await supabase.from("blog_post_resources" as any).delete().eq("id", id);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } else {
      toast({ title: "Deleted", description: "Resource removed." });
      fetchResources();
    }
  };

  const handleAIGenerate = async () => {
    setGenerating(true);
    try {
      toast({ title: "Generating blueprint", description: "Preparing your premium guide. This can take 30–60s." });
      const { data, error } = await supabase.functions.invoke("generate-premium-guide", {
        body: { blogPostId },
      });
      if (error) throw error;
      if (!data?.markdown) throw new Error("No blueprint content returned");

      setPreviewMarkdown(data.markdown);
      setPreviewTitle(data.title || "Premium Blueprint");
      setPreviewOpen(true);
      toast({ title: "Draft ready", description: "Review and edit the blueprint, then save as PDF." });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Generation failed", description: err.message });
    } finally {
      setGenerating(false);
    }
  };

  const handleSavePdf = async () => {
    if (!previewMarkdown.trim()) {
      toast({ variant: "destructive", title: "Empty content", description: "Cannot save an empty blueprint." });
      return;
    }
    setSavingPdf(true);
    try {
      toast({ title: "Rendering PDF", description: "Converting blueprint into a premium PDF..." });
      const blob = await renderPremiumGuidePdf(previewMarkdown, previewTitle || "Premium Blueprint", {
        subtitle: previewSubtitle,
        accentColor,
        includeReflection,
      });
      const sizeStr = formatBytes(blob.size);

      const filename = `premium-blueprint-${Date.now()}.pdf`;
      const path = `${blogPostId}/${filename}`;
      const { error: upErr } = await supabase.storage
        .from("blog-resources")
        .upload(path, blob, { upsert: false, contentType: "application/pdf" });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("blog-resources").getPublicUrl(path);

      const { error: insErr } = await supabase.from("blog_post_resources" as any).insert({
        blog_post_id: blogPostId,
        name: `Premium Blueprint — ${previewTitle || "Guide"}`,
        description: "Premium implementation blueprint with modules, frameworks, exercises, quiz recap and FAQ.",
        file_url: pub.publicUrl,
        file_format: "PDF",
        file_size: sizeStr,
        order_index: resources.length,
      });
      if (insErr) throw insErr;

      toast({ title: "Premium guide saved", description: "Blueprint added to this blog's resources." });
      setPreviewOpen(false);
      setPreviewMarkdown("");
      setPreviewTitle("");
      fetchResources();
    } catch (err: any) {
      toast({ variant: "destructive", title: "Save failed", description: err.message });
    } finally {
      setSavingPdf(false);
    }
  };

  const previewHtml = useMemo(
    () => marked.parse(previewMarkdown || "", { gfm: true, breaks: true }) as string,
    [previewMarkdown],
  );
  const lintIssues = useMemo(() => lintMarkdown(previewMarkdown), [previewMarkdown]);
  const lintErrors = lintIssues.filter((i) => i.level === "error");
  const lintWarnings = lintIssues.filter((i) => i.level === "warning");

  if (blogPostId === "new") {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Save the blog post first, then return here to add Premium Guide resources.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-primary/40 bg-gradient-to-br from-primary/5 via-background to-background">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Premium Blueprint Generator
          </CardTitle>
          <CardDescription>
            Create a premium, course-grade PDF blueprint from this blog post, its roadmap, and quiz —
            then save it directly as a downloadable resource.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleAIGenerate} disabled={generating || uploading} size="lg">
            {generating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating premium blueprint...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Premium Guide
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            Generation takes ~30–60 seconds and produces a multi-page PDF with
            modules, frameworks, exercises, quiz recap and FAQ.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileUp className="h-5 w-5" />
            {editingId ? "Edit Resource" : "Add New Resource"}
          </CardTitle>
          <CardDescription>
            Attach guides, PDFs, templates, or any downloadable files to this blog's Premium Guide.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Resource Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Complete YouTube Growth Playbook"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="Short description shown to readers"
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>File Format</Label>
              <Input
                value={form.file_format}
                onChange={(e) => setForm({ ...form, file_format: e.target.value })}
                placeholder="PDF, ZIP, DOCX..."
              />
            </div>
            <div className="space-y-2">
              <Label>File Size</Label>
              <Input
                value={form.file_size}
                onChange={(e) => setForm({ ...form, file_size: e.target.value })}
                placeholder="2.4 MB"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>File URL *</Label>
            <Input
              value={form.file_url}
              onChange={(e) => setForm({ ...form, file_url: e.target.value })}
              placeholder="https://... (upload below or paste a URL)"
            />
          </div>
          <div className="space-y-2">
            <Label>Upload File</Label>
            <Input
              type="file"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFileUpload(f);
              }}
            />
            {uploading && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Uploading...
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={uploading}>
              {editingId ? <Save className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
              {editingId ? "Save Changes" : "Add Resource"}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={resetForm}>
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Attached Resources ({resources.length})
          </CardTitle>
          <CardDescription>Resources shown in the Premium Guide section of this blog post.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : resources.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No resources yet. Add one above.</p>
          ) : (
            <div className="space-y-3">
              {resources.map((r) => (
                <div
                  key={r.id}
                  className="flex items-start justify-between gap-4 rounded-lg border border-border p-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{r.name}</p>
                    {r.description && (
                      <p className="text-sm text-muted-foreground mt-1">{r.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
                      {r.file_format && <span className="rounded bg-muted px-2 py-0.5">{r.file_format}</span>}
                      {r.file_size && <span>{r.file_size}</span>}
                      <a
                        href={r.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary underline truncate max-w-[300px]"
                      >
                        Open <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => handleEdit(r)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(r.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={(open) => !savingPdf && setPreviewOpen(open)}>
        <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="p-6 pb-4 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Review Premium Blueprint
            </DialogTitle>
            <DialogDescription>
              Edit the markdown on the left and watch the preview update on the right. When ready, save it as a PDF resource.
            </DialogDescription>
            <div className="grid gap-3 pt-2 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pg-title">Blueprint title (cover page)</Label>
                <Input
                  id="pg-title"
                  value={previewTitle}
                  onChange={(e) => setPreviewTitle(e.target.value)}
                  placeholder="Premium Blueprint title"
                  disabled={savingPdf}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pg-subtitle">Cover subtitle</Label>
                <Input
                  id="pg-subtitle"
                  value={previewSubtitle}
                  onChange={(e) => setPreviewSubtitle(e.target.value)}
                  placeholder="e.g. Premium Blueprint • RunoBlog Learning System"
                  disabled={savingPdf}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="pg-accent">Cover accent color</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    id="pg-accent"
                    type="color"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    disabled={savingPdf}
                    className="h-10 w-14 cursor-pointer rounded-md border border-input bg-background p-1"
                  />
                  <Input
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    disabled={savingPdf}
                    className="w-32 font-mono text-xs"
                  />
                  <div className="flex flex-wrap gap-1">
                    {ACCENT_PRESETS.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => setAccentColor(p.value)}
                        disabled={savingPdf}
                        title={p.name}
                        className="h-7 w-7 rounded-full border-2 border-border transition hover:scale-110"
                        style={{ background: p.value, borderColor: accentColor === p.value ? "hsl(var(--primary))" : undefined }}
                        aria-label={`Use ${p.name} accent`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 flex items-start justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="pg-reflect" className="cursor-pointer">
                    Auto-add Reflection &amp; Weekly Goals
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Append a Reflection prompt + 7-day execution worksheet after every chapter (H2 heading) in the PDF.
                  </p>
                </div>
                <input
                  id="pg-reflect"
                  type="checkbox"
                  checked={includeReflection}
                  onChange={(e) => setIncludeReflection(e.target.checked)}
                  disabled={savingPdf}
                  className="mt-1 h-5 w-5 cursor-pointer accent-primary"
                />
              </div>

              <div className="space-y-2 md:col-span-2 rounded-md border border-dashed border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Cover presets
                  </Label>
                  <span className="text-[10px] text-muted-foreground">
                    {coverPresets.length} saved
                  </span>
                </div>

                {coverPresets.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {coverPresets.map((p) => (
                      <div
                        key={p.id}
                        className={`group flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                          selectedPresetId === p.id
                            ? "border-primary bg-primary/10"
                            : "border-border hover:bg-muted"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleApplyPreset(p.id)}
                          disabled={savingPdf}
                          className="flex items-center gap-1.5"
                          title={`${p.subtitle} • ${p.accentColor}`}
                        >
                          <span
                            className="h-3 w-3 rounded-full border border-border"
                            style={{ background: p.accentColor }}
                          />
                          <span className="font-medium">{p.name}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeletePreset(p.id)}
                          disabled={savingPdf}
                          className="opacity-50 hover:opacity-100 hover:text-destructive"
                          aria-label={`Delete preset ${p.name}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-1">
                  <Input
                    value={newPresetName}
                    onChange={(e) => setNewPresetName(e.target.value)}
                    placeholder="Preset name (e.g. YouTube Series)"
                    disabled={savingPdf}
                    className="h-9 flex-1 min-w-[160px] text-xs"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleSavePreset}
                    disabled={savingPdf || !newPresetName.trim()}
                  >
                    <Save className="mr-1.5 h-3.5 w-3.5" />
                    Save current as preset
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Saves the current subtitle and accent color. Reusable across all blog posts on this device.
                </p>
              </div>
            </div>

            {previewMarkdown && (
              <div className="pt-3">
                {lintErrors.length === 0 && lintWarnings.length === 0 ? (
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>Markdown looks great</AlertTitle>
                    <AlertDescription>No issues detected. Ready to render.</AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant={lintErrors.length > 0 ? "destructive" : "default"}>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>
                      {lintErrors.length > 0
                        ? `${lintErrors.length} error${lintErrors.length > 1 ? "s" : ""} • `
                        : ""}
                      {lintWarnings.length} warning{lintWarnings.length === 1 ? "" : "s"}
                    </AlertTitle>
                    <AlertDescription>
                      <ul className="mt-1 list-disc pl-5 space-y-0.5 text-xs max-h-32 overflow-auto">
                        {lintIssues.map((issue, i) => (
                          <li key={i} className={issue.level === "error" ? "font-semibold" : ""}>
                            <span className="uppercase mr-1 text-[10px] tracking-wider opacity-70">{issue.level}</span>
                            {issue.message}
                          </li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </DialogHeader>

          <Tabs defaultValue="split" className="flex-1 flex flex-col min-h-0">
            <TabsList className="mx-6 mt-4 self-start">
              <TabsTrigger value="split"><Eye className="h-4 w-4 mr-1" /> Split</TabsTrigger>
              <TabsTrigger value="edit"><FileText className="h-4 w-4 mr-1" /> Edit only</TabsTrigger>
              <TabsTrigger value="preview"><Eye className="h-4 w-4 mr-1" /> Preview only</TabsTrigger>
            </TabsList>

            <TabsContent value="split" className="flex-1 min-h-0 m-0 px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
                <Textarea
                  value={previewMarkdown}
                  onChange={(e) => setPreviewMarkdown(e.target.value)}
                  disabled={savingPdf}
                  className="font-mono text-xs h-full resize-none"
                />
                <div
                  className="prose prose-sm dark:prose-invert max-w-none h-full overflow-auto rounded-md border bg-card p-4"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>
            </TabsContent>

            <TabsContent value="edit" className="flex-1 min-h-0 m-0 px-6 py-4">
              <Textarea
                value={previewMarkdown}
                onChange={(e) => setPreviewMarkdown(e.target.value)}
                disabled={savingPdf}
                className="font-mono text-xs h-full resize-none"
              />
            </TabsContent>

            <TabsContent value="preview" className="flex-1 min-h-0 m-0 px-6 py-4">
              <div
                className="prose prose-sm dark:prose-invert max-w-none h-full overflow-auto rounded-md border bg-card p-6"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </TabsContent>
          </Tabs>

          <DialogFooter className="p-6 pt-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={savingPdf}>
              Cancel
            </Button>
            <Button onClick={handleAIGenerate} variant="secondary" disabled={generating || savingPdf}>
              {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Regenerate
            </Button>
            <Button onClick={handleSavePdf} disabled={savingPdf || !previewMarkdown.trim() || lintErrors.length > 0}>
              {savingPdf ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving PDF...</>
              ) : (
                <><Save className="mr-2 h-4 w-4" /> Render & Save PDF</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
